import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
}

// Primary actions speak in the display face; the rest stay in the body face.
const variantStyles: Record<Variant, string> = {
  primary:
    'bg-sage-700 text-sage-50 font-display font-normal hover:bg-sage-800 active:bg-sage-900 disabled:bg-sage-300 disabled:text-sage-50',
  secondary:
    'bg-transparent text-gray-900 border border-DEFAULT font-semibold hover:bg-gray-900/[0.07] active:bg-gray-900/[0.14] disabled:opacity-45',
  ghost:
    'bg-transparent text-gray-600 font-semibold hover:bg-gray-900/[0.07] hover:text-gray-900 disabled:opacity-45',
  danger:
    'bg-red-600 text-gray-50 font-semibold hover:bg-red-700 disabled:opacity-45',
}

const sizeStyles: Record<Size, string> = {
  sm: 'h-8 px-3.5 text-[13px]',
  md: 'h-10 px-5 text-[14px]',
  lg: 'h-12 px-6 text-[15px]',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-full transition-colors',
        'disabled:cursor-not-allowed',
        variantStyles[variant],
        sizeStyles[size],
        className,
      )}
      {...rest}
    />
  )
})
